import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { easeInOutCubic } from '../../engine/math/easing';
import { MeshData, Color, translate, setEmissive } from '../../engine/geo/mesh';
import { box, cylinder, cone, combine } from '../../engine/geo/primitives';
import type { BoxCollider } from '../../engine/physics/StaticWorld';
import type { Ctx } from '../Ctx';
import type { ItemId } from '../items/ItemDefs';
import type { Items } from '../items/Items';

export type KeepId = 'gold' | 'white' | 'black';

export interface KeepDef {
  id: KeepId;
  keyId: ItemId;
  /** Center of the interior floor. */
  pos: Vec3;
  w: number; d: number; h: number; wall: number;
  color: Color; trim: Color;
  towers: boolean;
  /** Gate is centered on the +Z wall. */
}

export interface Keep {
  def: KeepDef;
  /** Where the keep's item sits (on the dais). */
  dais: Vec3;
  gatePos: Vec3;
  bars: Transform;
  gateCollider: BoxCollider;
  open: boolean;
  opening: boolean;
  openT: number;
  interior: AABB;
  floorY: number;
}

export const KEEP_DEFS: Record<KeepId, Omit<KeepDef, 'pos'>> = {
  gold: { id: 'gold', keyId: 'goldKey', w: 16, d: 16, h: 11, wall: 1.4, color: [0.82, 0.72, 0.5], trim: [0.95, 0.78, 0.35], towers: true },
  white: { id: 'white', keyId: 'whiteKey', w: 11, d: 11, h: 14, wall: 1.1, color: [0.84, 0.82, 0.78], trim: [0.95, 0.95, 0.9], towers: true },
  black: { id: 'black', keyId: 'blackKey', w: 22, d: 14, h: 6, wall: 1.6, color: [0.12, 0.11, 0.13], trim: [0.5, 0.15, 0.2], towers: false },
};

const GATE_W = 3.2, GATE_H = 4.2;

export function buildKeep(ctx: Ctx, def: KeepDef): Keep {
  const { pos, w, d, h, wall, color, trim } = def;
  const floorY = ctx.statics.terrainHeight(pos.x, pos.z);
  const parts: MeshData[] = [];
  const hw = w / 2, hd = d / 2;
  const st = ctx.statics;
  // floor slab
  parts.push(translate(box(w + wall * 2, 0.3, d + wall * 2, [color[0] * 0.8, color[1] * 0.8, color[2] * 0.8]), pos.x, floorY - 0.15, pos.z));
  // walls: west, east, north (back), south split around the gate
  const wallBox = (cx: number, cz: number, sx: number, sz: number, hh = h, y0 = floorY) => {
    parts.push(translate(box(sx, hh, sz, color), cx, y0 + hh / 2, cz));
    st.addBox(AABB.fromMinMax(cx - sx / 2, y0 - 1, cz - sz / 2, cx + sx / 2, y0 + hh, cz + sz / 2), 'keep');
  };
  wallBox(pos.x - hw - wall / 2, pos.z, wall, d + wall * 2);
  wallBox(pos.x + hw + wall / 2, pos.z, wall, d + wall * 2);
  wallBox(pos.x, pos.z - hd - wall / 2, w, wall);
  const sideW = (w - GATE_W) / 2;
  wallBox(pos.x - hw + sideW / 2, pos.z + hd + wall / 2, sideW, wall);
  wallBox(pos.x + hw - sideW / 2, pos.z + hd + wall / 2, sideW, wall);
  // lintel above the gate
  wallBox(pos.x, pos.z + hd + wall / 2, GATE_W, wall, h - GATE_H, floorY + GATE_H);
  // roof slab with a gap (the shaft of light comes through it)
  parts.push(translate(box(w + wall * 2, 0.6, d + wall * 2, [color[0] * 0.7, color[1] * 0.7, color[2] * 0.7]), pos.x, floorY + h + 0.3, pos.z));
  st.addBox(AABB.fromMinMax(pos.x - hw - wall, floorY + h, pos.z - hd - wall, pos.x + hw + wall, floorY + h + 0.6, pos.z + hd + wall), 'keep');
  // crenellations
  const step = 2.2;
  for (let x = -hw - wall; x <= hw + wall; x += step) {
    parts.push(translate(box(1.1, 1.0, wall, trim), pos.x + x, floorY + h + 1.1, pos.z - hd - wall / 2));
    parts.push(translate(box(1.1, 1.0, wall, trim), pos.x + x, floorY + h + 1.1, pos.z + hd + wall / 2));
  }
  for (let z = -hd - wall; z <= hd + wall; z += step) {
    parts.push(translate(box(wall, 1.0, 1.1, trim), pos.x - hw - wall / 2, floorY + h + 1.1, pos.z + z));
    parts.push(translate(box(wall, 1.0, 1.1, trim), pos.x + hw + wall / 2, floorY + h + 1.1, pos.z + z));
  }
  if (def.towers) {
    const tr = wall * 1.6, th = h + 4;
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const cx = pos.x + sx * (hw + wall), cz = pos.z + sz * (hd + wall);
      parts.push(translate(cylinder(tr, tr * 1.05, th, 9, color), cx, floorY + th / 2, cz));
      parts.push(translate(cone(tr * 1.25, 2.6, 9, trim), cx, floorY + th + 1.3, cz));
      st.addCylinder(cx, cz, tr, floorY - 1, floorY + th, 'keep');
    }
  }
  // interior dais/pedestal spot and the shaft of light (emissive slanted slab, drawn transparent)
  parts.push(translate(box(2.2, 0.35, 2.2, trim), pos.x, floorY + 0.17, pos.z - hd * 0.45));
  st.addFloor(AABB.fromMinMax(pos.x - 1.1, floorY - 1, pos.z - hd * 0.45 - 1.1, pos.x + 1.1, floorY + 0.35, pos.z - hd * 0.45 + 1.1), 'dais');
  ctx.spawn('keep.' + def.id, new Transform(), combine(...parts));
  const shaft = setEmissive(box(1.6, h, 1.6, [1, 0.95, 0.8]), [1, 0.95, 0.8], 0.5);
  const shaftT = new Transform();
  shaftT.position.set(pos.x + 1.5, floorY + h / 2, pos.z - hd * 0.45);
  shaftT.rotation.setEuler(0, 0, 0.12);
  shaftT.snapPrev();
  ctx.spawn('keep.shaft.' + def.id, shaftT, shaft, { transparent: true, alpha: 0.12, castShadow: false });

  // portcullis bars
  const barsMesh: MeshData[] = [];
  const barColor: Color = def.id === 'black' ? [0.35, 0.1, 0.12] : [0.25, 0.22, 0.2];
  for (let i = 0; i < 6; i++) {
    barsMesh.push(translate(cylinder(0.07, 0.07, GATE_H, 6, barColor), -GATE_W / 2 + 0.3 + i * ((GATE_W - 0.6) / 5), GATE_H / 2, 0));
  }
  for (let j = 0; j < 3; j++) barsMesh.push(translate(box(GATE_W, 0.12, 0.12, barColor), 0, 0.5 + j * 1.6, 0));
  const bars = new Transform();
  const gatePos = new Vec3(pos.x, floorY, pos.z + hd + wall / 2);
  bars.position.copy(gatePos);
  bars.snapPrev();
  ctx.spawn('keep.gate.' + def.id, bars, combine(...barsMesh));
  const gateCollider = st.addBox(AABB.fromMinMax(gatePos.x - GATE_W / 2, floorY - 1, gatePos.z - 0.3, gatePos.x + GATE_W / 2, floorY + GATE_H, gatePos.z + 0.3), 'gate');
  const interior = AABB.fromMinMax(pos.x - hw, floorY - 1, pos.z - hd, pos.x + hw, floorY + h, pos.z + hd + wall + 1);
  return { def, dais: new Vec3(pos.x, floorY + 0.35, pos.z - hd * 0.45), gatePos, bars, gateCollider, open: false, opening: false, openT: 0, interior, floorY };
}

/** Key proximity opens gates (§4.1). Call every sim step. */
export function updateKeeps(ctx: Ctx, keeps: Keep[], items: Items, dt: number, onOpen?: (k: Keep) => void): void {
  for (const k of keeps) {
    if (k.open) continue;
    if (k.opening) {
      k.openT += dt;
      const t = k.openT;
      if (t < 0.6) {
        // rattle
        k.bars.position.x = k.gatePos.x + Math.sin(t * 60) * 0.03 * (1 - t / 0.6);
      } else {
        const r = easeInOutCubic(Math.min(1, (t - 0.6) / 1.5));
        k.bars.position.x = k.gatePos.x;
        k.bars.position.y = k.gatePos.y + r * (GATE_H - 0.15);
        if (r >= 1) { k.open = true; k.opening = false; k.gateCollider.enabled = false; }
      }
      continue;
    }
    const key = items.get(k.def.keyId);
    if (!key) continue;
    if (key.loc !== 'held' && key.loc !== 'ground') continue;
    const kp = items.worldPos(key, _v);
    if (kp.distanceToXZ(k.gatePos) < 1.6 && Math.abs(kp.y - k.gatePos.y) < 3) {
      k.opening = true; k.openT = 0;
      if (key.loc === 'held') items.dropAt(_v2.copy(k.gatePos).add(_v3.set(key.t.position.x < k.gatePos.x ? -1.2 : 1.2, 0, 0.9)));
      ctx.sfx.gate(k.gatePos);
      ctx.fx.shake(0.12, 0.6);
      onOpen?.(k);
    }
  }
}
const _v = new Vec3(), _v2 = new Vec3(), _v3 = new Vec3();
